import React, { useState } from "react";
import { CheckCircle } from "@mui/icons-material";
import { Alert, AlertTitle, Box, Button, Paper } from "@mui/material";

import { Progress } from "ui";

import { useCustomWorkflow } from "fond/hooks/useCustomWorkflow";
import { LayerStatus } from "fond/hooks/useGetImportLayers";
import useWorkflowErrorLogs from "fond/hooks/useWorkflowErrorLogs";
import BomModal from "fond/project/bom/BomModal";
import { MuPowerSolverLayerIds, Project, WORKFLOW_KEYS } from "fond/types";
import { useAppSelector } from "fond/utils/hooks";
import { isImportInProgress } from "fond/utils/layers";
import { LinearProgressWithLabel, StackedNavigationButton, SvgIcon } from "fond/widgets";

const stageConfig: Record<string, { label: string }> = {
  power: { label: "Generating power design" },
  bom: { label: "Generating bill of materials" },
};

interface PowerSolverPanelProps {
  isReadOnly?: boolean;
  canRun: boolean;
  solverLayers: Map<string, LayerStatus>;
  project: Project;
}

const PowerSolverPanel: React.FC<PowerSolverPanelProps> = ({ isReadOnly, canRun, solverLayers, project }) => {
  const versionId = useAppSelector((state) => state.project.versionId);
  const [isBomModalOpen, setIsBomModalOpen] = useState(false);

  const { workflowState, stages, runWorkflow, cancelWorkflow, logs } = useCustomWorkflow({
    versionId,
    key: WORKFLOW_KEYS.MU_POWER.POWER_SOLVER,
  });
  const { errorLogsInline, logsModal } = useWorkflowErrorLogs(logs);

  const isInProgress = workflowState === "STARTING" || workflowState === "IN_PROGRESS";

  const isAnyUploading = Array.from(solverLayers.values()).some((layer) => isImportInProgress(layer.status));

  const hasSourceHubs = !!solverLayers.get(MuPowerSolverLayerIds.sourceHubs)?.layer;
  const hasBlockers =
    !!solverLayers.get(MuPowerSolverLayerIds.primaryBlockers)?.layer || !!solverLayers.get(MuPowerSolverLayerIds.secondaryBlockers)?.layer;
  const hasFootprint =
    !!solverLayers.get(MuPowerSolverLayerIds.primaryFootprint)?.layer || !!solverLayers.get(MuPowerSolverLayerIds.secondaryFootprint)?.layer;
  const hasTransformers = !!solverLayers.get(MuPowerSolverLayerIds.transformerHubs)?.layer;

  const rightAdornment = (complete: boolean) => (complete ? <CheckCircle color="success" sx={{ fontSize: 18 }} /> : undefined);

  return (
    <>
      {!canRun && workflowState === undefined && (
        <Alert severity="info" variant="compact" sx={{ mb: 1 }}>
          <AlertTitle>Data preparation required</AlertTitle>
          Please complete data preparation before running the power solver.
        </Alert>
      )}

      {canRun && !hasSourceHubs && workflowState === undefined && (
        <Alert severity="info" variant="compact" sx={{ mb: 1 }}>
          <AlertTitle>Source hubs required</AlertTitle>
          Please add source hubs to continue.
        </Alert>
      )}

      {canRun && hasSourceHubs && workflowState === undefined && (
        <Alert severity="info" variant="compact" sx={{ mb: 1 }}>
          Design generation is ready to start.
        </Alert>
      )}

      {workflowState === "ERROR" && (
        <Alert severity="error" variant="compact" sx={{ mb: 1 }}>
          <AlertTitle>Power solver failed</AlertTitle>
          There was an error running the power solver. Please try again.
          {errorLogsInline}
        </Alert>
      )}

      {workflowState === "COMPLETE" && (
        <Alert severity="success" variant="compact" sx={{ mb: 1 }}>
          Power design complete.
        </Alert>
      )}

      {stages && (isInProgress || workflowState === "ERROR") && (
        <Progress steps={stages.map(({ key, status }) => ({ ID: key, Label: stageConfig[key]?.label ?? key, Status: status }))} />
      )}

      {!isReadOnly && !isInProgress && (
        <Box>
          <StackedNavigationButton
            icon={<SvgIcon icon="points" />}
            title="Source Hubs"
            target={MuPowerSolverLayerIds.sourceHubs}
            sx={{ m: 0, mt: 1 }}
            checked={hasSourceHubs}
            rightElement={rightAdornment(hasSourceHubs)}
          />
          <StackedNavigationButton
            icon={<SvgIcon icon="line" />}
            title="Blockers"
            target="mu-blockers"
            sx={{ m: 0, mt: 1 }}
            checked={hasBlockers}
            rightElement={rightAdornment(hasBlockers)}
          />
          <StackedNavigationButton
            icon={<SvgIcon icon="line" />}
            title="Footprint"
            target="mu-footprint"
            sx={{ m: 0, mt: 1 }}
            checked={hasFootprint}
            rightElement={rightAdornment(hasFootprint)}
          />
          {workflowState === "COMPLETE" && (
            <StackedNavigationButton
              icon={<SvgIcon icon="points" />}
              title="Transformers"
              target="mu-transformers"
              sx={{ m: 0, mt: 1 }}
              checked={hasTransformers}
              rightElement={rightAdornment(hasTransformers)}
            />
          )}
        </Box>
      )}

      {workflowState === "COMPLETE" && (
        <Button fullWidth variant="outlined" size="small" sx={{ mt: 2 }} onClick={() => setIsBomModalOpen(true)}>
          View bill of materials
        </Button>
      )}

      {!isInProgress && (
        <Button
          fullWidth
          variant="contained"
          size="small"
          sx={{ my: 2 }}
          disabled={!runWorkflow || !canRun || !hasSourceHubs || isInProgress || isAnyUploading || isReadOnly}
          onClick={() => runWorkflow?.()}
        >
          {workflowState === "COMPLETE" ? "Re-run design generation" : "Start design generation"}
        </Button>
      )}

      {isInProgress && (
        <Button
          fullWidth
          variant="contained"
          size="small"
          sx={{ my: 2 }}
          disabled={!cancelWorkflow || !canRun || !isInProgress || isReadOnly}
          onClick={() => cancelWorkflow?.()}
        >
          Cancel
        </Button>
      )}

      {isAnyUploading && (
        <Paper variant="outlined" sx={{ py: 2, px: 2.75, mt: 2 }}>
          <LinearProgressWithLabel color="primary" variant="indeterminate" title="Importing layer" subtext="Processing..." />
        </Paper>
      )}

      {logsModal}

      {isBomModalOpen && <BomModal project={project} architecture={null} onClose={() => setIsBomModalOpen(false)} />}
    </>
  );
};

export default PowerSolverPanel;
